import { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { io } from "socket.io-client";
import { URL } from "../store/utils";
import { applyNotificationNew } from "../store/storageRoles/notificationsApi";
import { handleSocketSessionExpiry } from "../utils/dashboardUtils";
import useLatestToken, { attachTokenOnReconnect } from "./useLatestToken";

/**
 * İş Planlama bildirimleri için Socket.IO bağlantısı (zil).
 * Contract: BayinerIOT-Workspace-/docs/api-contracts/tasks.md → "Bildirimler (1.2)".
 *
 * Kimlik doğrulama dashboard ile aynı: handshake `query.token`; server doğrulayınca
 * "authenticated" yayar ve bağlantıyı `user:<userId>` odasına katar. `notification:new`
 * { notification, unreadCount } gelince RTK cache'leri güncellenir.
 *
 * Kendi bağlantısını açar (dashboard socket'ine dokunmaz). `connected` false iken zil
 * okunmamış sayısını 60 sn'de bir sorgular. Server yetkisiz kullanıcıya reason'sız "error"
 * yayıp bağlantıyı keser ("io server disconnect" → otomatik yeniden bağlanma yok); bu durumda
 * sessizce polling'e düşülür. Çağıran, yalnız socket'e kabul edilecek kullanıcılar için token verir.
 *
 * @param {string|null} token — null/boş ise bağlanmaz (yetkisiz kullanıcı).
 */
export default function useNotificationSocket(token) {
  const { hasToken, tokenRef } = useLatestToken(token);
  const store = useStore();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!hasToken) return undefined;

    const socket = io(URL.url, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      reconnectionDelayMax: 30000,
      query: { token: tokenRef.current },
    });

    attachTokenOnReconnect(socket, tokenRef); // #142

    const onNew = (payload) => applyNotificationNew(store.dispatch, store.getState, payload);
    // Server doğrulamayı bitirip `user:<id>` odasına katınca "authenticated" yayar; dinleme o
    // anda başlar (yeniden bağlanmada çift kayıt olmasın diye önce off).
    socket.on("authenticated", () => {
      socket.off("notification:new", onNew);
      socket.on("notification:new", onNew);
      setConnected(true);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));
    socket.on("error", (payload) => {
      const reason = payload?.reason;
      if (!reason) return;
      setConnected(false);
      handleSocketSessionExpiry(reason);
    });

    return () => {
      setConnected(false);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [hasToken, tokenRef, store]);

  return { connected };
}
